import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Row, RowState } from "./Row";
import { Clue, clue, CluedLetter, fibclue } from "./clue";
import { Keyboard } from "./Keyboard";
import { maxGuesses } from "./util";
import { makePuzzle } from "./Game";
import { fetchGameStateForPuzzle, longPollGameState, RemoteGameState } from "./gameStateApi";

interface WatchViewProps {
  puzzleId: string;
  colorBlind: boolean;
  keyboardLayout: string;
  targetUserId?: string;
  watchLabel?: string;
  onStatusChange?: (status: "in_progress" | "won" | "lost") => void;
  children?: ReactNode;
}

export function WatchView({ puzzleId, colorBlind, keyboardLayout, targetUserId, watchLabel, onStatusChange, children }: WatchViewProps) {
  const [remote, setRemote] = useState<RemoteGameState | null>(null);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const puzzleNum = parseInt(puzzleId.replace("practice-", ""), 10);
  const isPractice = puzzleId.startsWith("practice-");
  const puzzle = !isNaN(puzzleNum) ? makePuzzle(puzzleNum) : null;

  // Status change notification — MUST be before any early returns (React hooks rules)
  useEffect(() => {
    if (onStatusChange && remote) {
      onStatusChange(remote.metadata.status);
    }
  }, [remote?.metadata.status]);

  // Data fetching
  useEffect(() => {
    let active = true;

    async function run() {
      if (targetUserId) {
        const data = await longPollGameState(targetUserId, puzzleId, null);
        if (!active) return;
        if (data) {
          setRemote(data);
          setError(false);
          await longPollLoop(targetUserId, data.updated_at || null);
        } else {
          setError(true);
        }
        return;
      }

      const initial = await fetchGameStateForPuzzle(puzzleId);
      if (!active) return;
      if (!initial || !initial.user_id) {
        setError(true);
        while (active) {
          await new Promise((r) => setTimeout(r, 3000));
          if (!active) return;
          const retry = await fetchGameStateForPuzzle(puzzleId);
          if (!active) return;
          if (retry && retry.user_id) {
            setRemote(retry);
            setError(false);
            await longPollLoop(retry.user_id, retry.updated_at || null);
            return;
          }
        }
        return;
      }

      setRemote(initial);
      setError(false);
      await longPollLoop(initial.user_id, initial.updated_at || null);
    }

    async function longPollLoop(userId: string, after: string | null) {
      while (active) {
        const controller = new AbortController();
        abortRef.current = controller;
        const data = await longPollGameState(userId, puzzleId, after, controller.signal);
        if (!active) return;
        if (data) {
          setRemote(data);
          after = data.updated_at || null;
        }
      }
    }

    run();

    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [puzzleId, targetUserId]);

  if (error && !remote) {
    return (
      <div className="Game">
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Waiting for player to start...
        </div>
      </div>
    );
  }

  if (!remote) {
    return (
      <div className="Game">
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Loading...
        </div>
      </div>
    );
  }

  const gameOver = remote.metadata.status === "won" || remote.metadata.status === "lost";
  const isLive = remote.metadata.status === "in_progress";

  const allGuesses = remote.state.guesses;
  const currentGuess = remote.state.currentGuess || "";
  const remoteFlags = remote.state.flags || [];

  // Compute clues locally from puzzle definition
  const cluedRows: CluedLetter[][] = puzzle
    ? allGuesses.map((g, i) => {
        const finalWinningGuess = remote.metadata.status === "won" && i === allGuesses.length - 1;
        return finalWinningGuess
          ? clue(g.word, puzzle.target)
          : fibclue(g.word, puzzle.target, puzzle.fibs[i]);
      })
    : [];

  // Build keyboard letter info and letter-true (locked) maps
  const letterTrue = new Map<string, boolean>();
  const letterInfo = new Map<string, Clue>();
  for (let i = 0; i < cluedRows.length; i++) {
    for (let j = 0; j < cluedRows[i].length; j++) {
      const { clue: c, letter } = cluedRows[i][j];
      if (c === undefined) break;
      if (remoteFlags[i] === j) continue; // skip flagged position
      const old = letterInfo.get(letter);
      if (old === undefined || c > old) {
        letterInfo.set(letter, c);
      }
    }
  }

  // Build a locks array (all false — we don't replicate locks, only flags)
  const emptyLocks: boolean[][] = Array(maxGuesses)
    .fill(undefined)
    .map(() => Array(5).fill(false));

  const totalRows = maxGuesses;

  const tableRows = Array(totalRows)
    .fill(undefined)
    .map((_, i) => {
      if (i < allGuesses.length && i < cluedRows.length) {
        const finalWinningGuess = remote.metadata.status === "won" && i === allGuesses.length - 1;
        return (
          <Row
            key={i}
            rowNum={i}
            fibPos={-1}
            flagPos={finalWinningGuess ? -1 : (remoteFlags[i] ?? -1)}
            rowState={RowState.LockedIn}
            locks={emptyLocks}
            cluedLetters={cluedRows[i]}
            clickHandler={() => {}}
            contextHandler={() => {}}
            annotation={"\u00A0"}
          />
        );
      }

      if (i === allGuesses.length && isLive) {
        const typingClues: CluedLetter[] = [];
        for (let c = 0; c < 5; c++) {
          typingClues.push(
            c < currentGuess.length
              ? { letter: currentGuess[c], clue: undefined }
              : { letter: "", clue: undefined }
          );
        }
        return (
          <Row
            key={i}
            rowNum={i}
            fibPos={-1}
            flagPos={-1}
            rowState={RowState.Editing}
            locks={emptyLocks}
            cluedLetters={typingClues}
            clickHandler={() => {}}
            contextHandler={() => {}}
            annotation={"\u00A0"}
          />
        );
      }

      const emptyClues: CluedLetter[] = Array(5)
        .fill(undefined)
        .map(() => ({ letter: "", clue: undefined as Clue | undefined }));
      return (
        <Row
          key={i}
          rowNum={i}
          fibPos={-1}
          flagPos={-1}
          rowState={RowState.Pending}
          locks={emptyLocks}
          cluedLetters={emptyClues}
          clickHandler={() => {}}
          contextHandler={() => {}}
          annotation={"\u00A0"}
        />
      );
    });

  return (
    <div className="Game">
      <div className="watch-banner-wrapper">
        <div className={`watch-banner${isLive ? " watch-banner--live" : ""}`}>
          {isLive && <span className="watch-dot" />}
          {watchLabel
            ? watchLabel
            : isLive
            ? "Watching Live"
            : gameOver
            ? remote.metadata.status === "won"
              ? "Replay \u2014 Player Won"
              : "Replay \u2014 Player Lost"
            : "Watch Mode"}
        </div>
      </div>
      <div className={`watch-border${isLive ? " watch-border--live" : ""}`}>
        <table
          className="Game-rows"
          tabIndex={0}
          aria-label="watched game board"
        >
          <tbody>{tableRows}</tbody>
        </table>
      </div>
      {children}

      {gameOver && puzzle && (
        <p
          role="alert"
          style={{ userSelect: "none", whiteSpace: "pre-wrap" }}
        >
          {remote.metadata.status === "won" ? "Player won!" : "Player lost."} The answer was {puzzle.target.toUpperCase()}.
        </p>
      )}

      <Keyboard
        layout={keyboardLayout}
        letterInfo={letterInfo}
        letterTrue={letterTrue}
        onKey={() => {}}
      />
    </div>
  );
}
